import { Menu, Transition } from "@headlessui/react";
import { DotsVerticalIcon } from "@heroicons/react/solid";
import React, { Fragment, useMemo } from "react";
import { useMembers } from "../../../../context/members";
import { useViewport } from "../../../../context/viewport";
import classNames from "../../../../utils/classNames";
import { breakpoints, Role } from "../../../../utils/constants";
import { Member } from "../../../../utils/types";

interface Props {
  disabled?: boolean;
  member: Member;
}

const MemberActionsButton: React.FC<Props> = ({ disabled, member }) => {
  const { updateMember, removeMember, removeInvitation } = useMembers();
  const { width } = useViewport();
  const isMobile = useMemo(() => width < breakpoints.sm, [width]);

  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          <div>
            <Menu.Button
              disabled={disabled}
              className="p-2 rounded-full bg-white flex items-center text-gray-400 hover:text-gray-600 focus:outline-none disabled:hover:bg-transparent disabled:cursor-default disabled:hover:text-gray-400 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white dark:hover:bg-gray-700 dark:disabled:hover:bg-gray-800"
            >
              <span className="sr-only">Open member actions</span>
              <DotsVerticalIcon className="h-5 w-5" aria-hidden="true" />
            </Menu.Button>
          </div>

          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              static
              className="z-50 origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800"
            >
              <div className="py-1">
                {isMobile && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => {
                          // TODO: ensure that the user has admin permissions in order to change this
                          updateMember(
                            member,
                            member.role === Role.admin
                              ? Role.member
                              : Role.admin
                          );
                        }}
                        className={classNames(
                          active
                            ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white"
                            : "text-gray-700 dark:bg-gray-800 dark:text-white",
                          "w-full flex justify-between px-4 py-2 text-sm"
                        )}
                      >
                        <span>
                          Assign{" "}
                          {member.role === Role.admin
                            ? Role.member
                            : Role.admin}{" "}
                          role
                        </span>
                      </button>
                    )}
                  </Menu.Item>
                )}

                {member.pending ? (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => {
                          removeInvitation(member);
                        }}
                        className={classNames(
                          active
                            ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white"
                            : "text-gray-700 dark:bg-gray-800 dark:text-white",
                          "w-full flex justify-between px-4 py-2 text-sm"
                        )}
                      >
                        <span>Revoke invitation</span>
                      </button>
                    )}
                  </Menu.Item>
                ) : (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => {
                          removeMember(member);
                        }}
                        className={classNames(
                          active
                            ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white"
                            : "text-gray-700 dark:bg-gray-800 dark:text-white",
                          "w-full flex justify-between px-4 py-2 text-sm"
                        )}
                      >
                        <span>Remove member</span>
                      </button>
                    )}
                  </Menu.Item>
                )}
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default MemberActionsButton;
